/**
 * 会议管理 - 会议列表
 *
 * @file list.js
 * @author yangwei(yangwei14@baidu.com)
 */

import {Component} from 'san';
import {Link} from 'san-router';
import _ from 'lodash';

import client from '../../../client';
import {asPage} from '../../helper';
import {formatTime, redirect, genRoomUrl, genOwnReadyPageUrl} from '../../common/utils';

import {Button, Table, Icon, Pager, Clipboard, notification} from '@baiducloud/bce-ui';
import {html} from '../../common/raw';
import {StatusText} from '../../common/enums';
import Partner from '../../components/partner';
import emptyImgUrl from '../../../../assert/empty.png';
import loadingImgUrl from '../../../../assert/loading.gif';


@asPage('/', '/meeting/list')
export default class extends Component {
    static components = {
        'ui-button': Button,
        'ui-table': Table,
        'ui-icon': Icon,
        'ui-pager': Pager,
        'ui-clipboard': Clipboard,
        'ui-partner': Partner,
        'router-link': Link
    }
    static filters = {
        format: time => formatTime(time * 1000),
        getTips: partner => _.reduce(partner, (str, item) => `${str} ${item.email || item.username}`, ''),
        getStatusText: text => StatusText[text],
        genRoomUrl
    }

    static template = html`
    <template>
        <div class="list-content meeting-list">
            <div class="list-header">会议管理</div>
            <div class="bulk">
                <div class="btn-left">
                    <ui-button
                        skin="primary"
                        on-click="startMeeting">
                        立即开会
                    </ui-button>
                    <ui-button
                        skin="default"
                        icon="plus"
                        on-click="scheduleMeeting"
                    >
                        预约会议
                    </ui-button>
                </div>
                <div class="btn-right">
                    <ui-icon class="{{uiMode === 'list' ? 'active' : ''}}"
                        name="data-sheet"
                        on-click="changeUI('list')" />
                    <ui-icon class="{{uiMode === 'card' ? 'active' : ''}}"
                        name="overview"
                        on-click="changeUI('card')"/>
                </div>
            </div>
            <div class="content-card" s-if="table.loading">
                <div class="hint-box loading">
                    <img src="${loadingImgUrl}"/>
                    <div>数据加载中，请稍后</div>
                </div>
            </div>
            <div class="content-card" s-elif="table.error">
                <div class="hint-box">
                    <img src="../../../../assert/error.png"/>
                    <div>数据加载失败，<a on-click="refresh">刷新重试</a></div>
                </div>
            </div>
            <div class="content-card" s-elif="!datasource || !datasource.length">
                <div class="hint-box">
                    <img src="${emptyImgUrl}"/>
                    <div>您还未创建会议，<router-link to="/meeting/create">马上创建></router-link></div>
                </div>
            </div>
            <template s-else>
                <div class="content-card" s-if="uiMode === 'list'">
                    <ui-table
                        schema="{{table.schema}}"
                        on-command="onCommand($event)"
                        on-sort="onSort($event)"
                        on-filter="onFilter($event)"
                        datasource="{{datasource}}"
                    >
                        <div slot="c-room_name">
                            <div class="room-name">
                                <span class="ellipsis text" title="{{row.room_name}}">{{row.room_name}}</span>
                                <span class="status normal"
                                    s-if="row.room_status === 2 || row.room_status === 3 || row.room_status === 4">
                                    <ui-icon name="video" />
                                </span>
                                <span class="status normal status-text"
                                    s-if="row.room_status === 2 || row.room_status === 3 || row.room_status === 4">
                                    {{row.room_status | getStatusText}}
                                </span>
                            </div>
                        </div>
                        <div slot="c-room_stime">
                            {{row.room_stime | format}}
                        </div>
                        <div slot="c-room_dtime">
                            {{row.room_dtime}} 分钟
                        </div>
                        <div slot="c-room_partner" class="partner-con">
                            <ui-partner s-if="row.room_partner.length" row="{{row}}" />
                        </div>
                        <div slot="c-operation">
                            <ui-button
                                s-if="row.room_status === 2"
                                skin="stringfy"
                                on-click="joinMeeting(row)"
                            >
                                立即入会
                            </ui-button>
                            <ui-button
                                s-if="row.room_status === 1"
                                skin="stringfy"
                                on-click="joinMeeting(row)"
                            >
                                提前入会
                            </ui-button>
                            <ui-clipboard
                                text="{{row | genRoomUrl}}"
                                tip-position="e"
                            >
                                <ui-button
                                    skin="stringfy">
                                    复制会邀
                                </ui-button>
                            </ui-clipboard>
                            <ui-button
                                skin="stringfy"
                                on-click="modifyMeeting(row)"
                            >
                                修改会议
                            </ui-button>
                            <ui-button
                                skin="stringfy"
                                on-click="cancelMeeting(row)"
                            >
                                取消会议
                            </ui-button>
                        </div>
                    </ui-table>
                </div>
                <div class="card-content" s-elif="uiMode === 'card'">
                    <div class="card-item" s-for="item in datasource">
                        <div class="card-header">
                            <div class="card-head-left">
                                <span class="card-head-text ellipsis" title="{{item.room_name}}">
                                    {{item.room_name}}</span>
                                <span
                                    class="status normal ml10"
                                    s-if="
                                        item.room_status === 2
                                        || item.room_status === 3
                                        || item.room_status === 4
                                    "
                                >
                                    <ui-icon name="video" />
                                </span>
                                <span
                                    class="status normal status-text"
                                    s-if="
                                        item.room_status === 2
                                        || item.room_status === 3
                                        || item.room_status === 4
                                    "
                                >
                                    {{item.room_status | getStatusText}}
                                </span>
                            </div>
                            <div class="card-head-right">
                                <ui-icon name="business-consult"  on-click="modifyMeeting(item)" />
                                <ui-button
                                    skin="stringfy"
                                    on-click="modifyMeeting(item)">
                                    修改
                                </ui-button>
                            </div>
                        </div>
                        <div class="card-time">
                            <ui-icon name="audit-progressing"></ui-icon>
                            <span slot="c-room_stime">
                                {{item.room_stime | format}}
                            </span>
                            <span slot="c-room_stime" class="stime">
                                {{item.room_dtime}} 分钟
                            </span>
                        </div>
                        <div class="card-partner partner-con">
                            <ui-icon name="collaborator" />
                            <ui-partner s-if="item.room_partner.length" row="{{item}}" />
                        </div>
                        <div class="card-desc ellipsis" title="{{row.room_desc}}">描述信息：{{item.room_desc}}</div>
                        <div class="card-footer">
                            <ui-button
                                s-if="item.room_status === 1"
                                skin="default"
                                on-click="joinMeeting(item)">
                                提前入会
                            </ui-button>
                            <ui-button
                                s-if="item.room_status === 2"
                                skin="default"
                                on-click="joinMeeting(item)">
                                立即入会
                            </ui-button>
                            <ui-clipboard
                                text="{{item | genRoomUrl}}"
                                tip-position="e"
                                layer-style="{{copyUrlLayerStyle}}"
                            >
                                <ui-button
                                    skin="default">
                                    复制会邀
                                </ui-button>
                            </ui-clipboard>
                            <ui-button
                                skin="default"
                                on-click="cancelMeeting(item)">
                                取消会议
                            </ui-button>
                        </div>
                    </div>
                </div>
                <ui-pager
                    s-if="false"
                    size="{{pager.size}}"
                    page="{{pager.page}}"
                    count="{{pager.count}}"
                    on-change="onPagerChange($event)"
                />
            </template>
        </div>
    </template>
    `;

    initData() {
        return {
            table: {
                select: false,
                schema: [
                    {name: 'room_name', label: '会议主题', width: 240},
                    {name: 'room_stime', label: '开始时间', width: 160},
                    {name: 'room_dtime', label: '会议时长', width: 140},
                    {name: 'room_partner', label: '参与人员'},
                    {name: 'room_desc', label: '描述', width: 160},
                    {name: 'operation', label: '操作', freezed: true, width: 292}
                ],
                loading: false
            },
            datasource: [],
            pager: {
                size: 10,
                page: 1,
                count: 10,
                datasource: [
                    {text: 10, value: 10},
                    {text: 50, value: 50},
                    {text: 100, value: 100}
                ]
            },
            uiMode: 'card',
            copyUrlLayerStyle: {
                width: '150px'
            }
        };
    }
    attached() {
        this.refresh();
    }

    refresh() {
        let payload = {};
        this.requestData(payload);
    }

    async requestData() {
        this.data.merge('table', {
            loading: true,
            error: false
        });
        try {
            const data = await client.getMeetingList();
            this.data.set('datasource', data.data.rows || []);
        } catch (ex) {
            this.data.set('table.error', true);
        }
        this.data.set('table.loading', false);
    }

    changeUI(args) {
        this.data.set('uiMode', args);
    }
    onPagerChange({pageNo}) {
        this.data.set('pager.page', pageNo);
    }
    joinStatus(row) {
    }
    scheduleMeeting() {
        redirect('#/meeting/create');
    }
    async startMeeting(row) {
        redirect('#/meeting/success?mode=startnow');
    }
    joinMeeting(row) {
        redirect(genOwnReadyPageUrl(row, true));
    }
    modifyMeeting(row) {
        redirect(`#/meeting/create?mode=modify&id=${row.room_id}`);
    }
    async cancelMeeting(row) {
        const data = await client.cancelMeeting(row.room_id);
        data && notification.success('会议取消成功！') && this.refresh();
    }
}